import React from 'react';
import { Card, Button, Text, Caption } from '../../design-system';

const T = {
  hand: '"Caveat", "Nunito", cursive',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  ink: '#2E2109',
  ink2: '#4A3510',
  inkMuted: '#6E5A38',
  inkFaint: '#A39068',
  orange: '#EA7C1F',
  orangeDark: '#C25F0E',
  paper: '#F7EFD8',
  paperDeep: '#EEE2BF',
  card: '#FFFBEC',
  rule: '#D7C9A1',
  r3: '10px',
  r4: '14px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
};

function formatRelativeTime(iso) {
  if (!iso) return 'never';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return 'never';
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ConnectedRepos({ repos, repoMeta, onDisconnect, onAdd }) {
  if (!repos || repos.length === 0) {
    return (
      <div style={{
        background: T.card,
        border: `1px solid ${T.rule}`,
        borderRadius: T.r4,
        boxShadow: T.sh1,
        padding: '32px 24px',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: T.hand,
          fontSize: '1.4rem',
          color: T.inkFaint,
          marginBottom: 12,
        }}>⎇</div>
        <div style={{
          fontFamily: T.sans,
          fontSize: '0.95rem',
          fontWeight: 700,
          color: T.ink,
          marginBottom: 6,
        }}>
          No repositories connected
        </div>
        <div style={{
          fontFamily: T.sans,
          fontSize: '0.85rem',
          color: T.inkMuted,
          marginBottom: 20,
        }}>
          Connect a GitHub repository to start detecting drift.
        </div>
        <button
          onClick={onAdd}
          style={{
            fontFamily: T.sans,
            fontSize: '0.875rem',
            fontWeight: 700,
            color: '#fff',
            backgroundColor: T.orange,
            border: 'none',
            borderRadius: T.r3,
            padding: '10px 20px',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = T.orangeDark; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = T.orange; }}
        >
          + Add repository
        </button>
      </div>
    );
  }

  if (repos.length === 1) {
    const r = repos[0];
    const key = `${r.owner}/${r.repo}`;
    const meta = repoMeta?.[key];
    return (
      <Card>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 16px',
            gap: 16,
          }}
        >
          <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: 'rgba(214,196,154,0.4)',
              border: '1px solid var(--color-border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18c-4.51 2-5-2-7-2"/><path d="M15 22v-4a3.37 3.37 0 0 0-.94-2.61C17.74 14.97 21 13.6 21 8.5a4.7 4.7 0 0 0-1.32-3.36c.13-.32.6-1.61-.13-3.36 0 0-1.07-.34-3.5 1.31a12.05 12.05 0 0 0-6.5 0C7.13 1.44 6.06 1.78 6.06 1.78c-.74 1.75-.27 3.04-.13 3.36A4.7 4.7 0 0 0 4.61 8.5C4.61 13.58 7.86 14.97 11.04 15.39A3.37 3.37 0 0 0 10 18v4"/>
              </svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontFamily: T.sans, fontSize: 16, fontWeight: 700,
                color: T.ink, letterSpacing: -0.2,
              }}>
                {key}
              </div>
              <div style={{ fontFamily: T.sans, fontSize: 13, color: T.inkMuted, marginTop: 2 }}>
                Watching {r.default_branch || 'main'} · last sync {formatRelativeTime(meta?.last_scanned_at)}
              </div>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => onDisconnect(r)}>
            Disconnect
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div style={{ padding: 8 }}>
        {repos.map((r, idx) => {
          const key = `${r.owner}/${r.repo}`;
          const meta = repoMeta?.[key];
          const isLast = idx === repos.length - 1;
          return (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Text>{key}</Text>
                <Caption>
                  {r.default_branch || 'main'} ·{' '}
                  {formatRelativeTime(meta?.last_scanned_at)}
                </Caption>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onDisconnect(r)}>
                Disconnect
              </Button>
            </div>
          );
        })}
        <div style={{ padding: 12, textAlign: 'center' }}>
          <Button variant="ghost" onClick={onAdd}>
            + Add repository
          </Button>
        </div>
      </div>
    </Card>
  );
}
